import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import { useTheme } from '../../contexts/ThemeContext';
import { Button, LoadingButton } from '../../components/ui/Button';
import { ContentSpinner, useToast } from '../../components/ui';

function AutoManagementProjectPage() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { theme } = useTheme();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [project, setProject] = useState(null);
  const [settings, setSettings] = useState(null);

  // Form states
  const [enabled, setEnabled] = useState(true);
  const [strategy, setStrategy] = useState('topic');
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [topics, setTopics] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [contentType, setContentType] = useState('text');
  const [persona, setPersona] = useState('');
  const [expertiseField, setExpertiseField] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('');

  const timeSlots = [
    '09:00', '10:00', '11:00', '12:00', '13:00',
    '14:00', '15:00', '16:00', '17:00', '18:00',
    '19:00', '20:00', '21:00', '22:00', '23:00'
  ];

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Fetch project details
      const response = await fetch(`/api/projects/${projectId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success && data.project) {
        setProject(data.project);

        // Parse contentDescription to get settings
        try {
          const desc = JSON.parse(data.project.contentDescription || '{}');
          setStrategy(desc.strategy || 'topic');
          setSelectedTimes(desc.postingTimes || []);
          setTopics(desc.topics || []);
          setKeywords(desc.keywords || []);
          setContentType(desc.contentType || 'text');
          setPersona(desc.persona || '');
          setExpertiseField(desc.expertiseField || '');
          setExperienceLevel(desc.experienceLevel || '');
          setSettings(desc);

          // Also fetch auto-management settings for enabled status
          if (desc.accountId) {
            const settingsRes = await fetch(`/api/auto-management/settings/${desc.accountId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const settingsData = await settingsRes.json();
            if (settingsData.success && settingsData.settings) {
              setEnabled(settingsData.settings.enabled);
            }
          }
        } catch (e) {
          console.error('Failed to parse project settings:', e);
        }
      } else {
        toast.error('Project not found');
        navigate('/projects');
      }
    } catch (error) {
      console.error('Failed to fetch project:', error);
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const handleTimeToggle = (time) => {
    setSelectedTimes(prev =>
      prev.includes(time) ? prev.filter(t => t !== time) : [...prev, time]
    );
  };

  const handleSave = async () => {
    if (!strategy) {
      toast.warning('Please select a content strategy');
      return;
    }

    if (selectedTimes.length === 0) {
      toast.warning('Please select at least one posting time');
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      const accountId = settings?.accountId;

      if (!accountId) {
        throw new Error('Account ID not found');
      }

      // 1. Update auto_management_settings
      await fetch('/api/auto-management/save-settings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          account_id: accountId,
          enabled: enabled,
          strategy: strategy,
          posting_times: selectedTimes,
          topics: topics,
          keywords: keywords,
          persona: persona,
          expertise_field: expertiseField,
          experience_level: experienceLevel,
          source_workflow: settings?.workflowConfig?.source || null,
          library: settings?.workflowConfig?.library || null,
          length: settings?.workflowConfig?.length || null,
          style: settings?.workflowConfig?.style || null,
          theme: settings?.workflowConfig?.theme || null,
          image_style: settings?.workflowConfig?.imageStyle || null
        })
      });

      // 2. Update accounts table
      await fetch(`/api/accounts/account-settings/id/${accountId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          publish_times: selectedTimes,
          content_type: contentType === 'carousel' ? '圖卡' : '純文',
          persona: persona ? `${persona}|${expertiseField}|${experienceLevel}` : null
        })
      });

      // 3. Update project
      const updatedDesc = {
        ...settings,
        strategy: strategy,
        postingTimes: selectedTimes,
        topics: topics,
        keywords: keywords,
        contentType: contentType,
        persona: persona,
        expertiseField: expertiseField,
        experienceLevel: experienceLevel
      };

      await fetch(`/api/projects/${projectId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contentDescription: JSON.stringify(updatedDesc)
        })
      });

      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Save failed:', error);
      toast.error(`Failed to save: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async () => {
    const newEnabled = !enabled;
    setEnabled(newEnabled);

    try {
      const token = localStorage.getItem('token');
      const accountId = settings?.accountId;

      if (accountId) {
        await fetch(`/api/auto-management/toggle/${accountId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ enabled: newEnabled })
        });

        toast.success(newEnabled ? 'Auto management enabled' : 'Auto management disabled');
      }
    } catch (error) {
      console.error('Toggle failed:', error);
      setEnabled(!newEnabled); // Revert
    }
  };

  if (loading) {
    return (
      <MainLayout activePage="projects">
        <div className="flex-1 flex items-center justify-center">
          <ContentSpinner />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout activePage="projects">
      <div className="flex-1 py-8 px-12 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={() => navigate('/projects')}
            variant="secondary"
            size="sm"
            icon="mdi:arrow-left"
            className="!p-2"
          />
          <div className="flex-1">
            <h1 className="m-0 text-[1.75rem] font-bold" style={{ color: theme.textPrimary }}>
              {project?.name || 'Auto Management Settings'}
            </h1>
            <p className="mt-1 mb-0 text-[0.9rem]" style={{ color: theme.textSecondary }}>
              {settings?.accountName} · {settings?.platform?.charAt(0).toUpperCase() + settings?.platform?.slice(1)}
            </p>
          </div>

          {/* Status Toggle */}
          <div
            className="flex items-center gap-3 py-3 px-5 rounded-lg"
            style={{
              background: enabled ? 'rgba(16, 185, 129, 0.1)' : theme.bgSecondary,
              border: `1px solid ${enabled ? '#10b981' : theme.borderColor}`
            }}
          >
            <span
              className="text-[0.9rem] font-medium"
              style={{ color: enabled ? '#10b981' : theme.textSecondary }}
            >
              {enabled ? 'Running' : 'Paused'}
            </span>
            <div
              onClick={handleToggleEnabled}
              className="w-11 h-6 rounded-xl relative cursor-pointer transition-all duration-300"
              style={{ background: enabled ? '#10b981' : '#cbd5e1' }}
            >
              <div
                className="w-[18px] h-[18px] rounded-full bg-white absolute top-[3px] transition-all duration-300"
                style={{ left: enabled ? '23px' : '3px' }}
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-2 gap-8">
          {/* Left Column */}
          <div>
            {/* Strategy Section */}
            <div
              className="rounded-xl p-6 mb-6"
              style={{ background: theme.bgSecondary, border: `1px solid ${theme.borderColor}` }}
            >
              <h3 className="m-0 mb-4 text-base font-semibold" style={{ color: theme.textPrimary }}>
                Content Strategy
              </h3>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { id: 'topic', icon: 'mdi:book-open-variant', label: 'Topic Based' },
                  { id: 'news', icon: 'mdi:newspaper', label: 'News Tracking' },
                  { id: 'random', icon: 'mdi:dice-multiple', label: 'Random Story' }
                ].map(item => (
                  <div
                    key={item.id}
                    onClick={() => setStrategy(item.id)}
                    className="p-4 rounded-lg cursor-pointer text-center transition-all duration-200"
                    style={{
                      background: strategy === item.id ? theme.buttonPrimary : 'transparent',
                      border: `1px solid ${strategy === item.id ? theme.buttonPrimary : theme.borderColor}`
                    }}
                  >
                    <Icon
                      icon={item.icon}
                      className="text-2xl mb-2"
                      style={{ color: strategy === item.id ? theme.buttonPrimaryText : theme.textSecondary }}
                    />
                    <div
                      className="text-[0.8rem] font-medium"
                      style={{ color: strategy === item.id ? theme.buttonPrimaryText : theme.textPrimary }}
                    >
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Content Type Section */}
            <div
              className="rounded-xl p-6 mb-6"
              style={{ background: theme.bgSecondary, border: `1px solid ${theme.borderColor}` }}
            >
              <h3 className="m-0 mb-4 text-base font-semibold" style={{ color: theme.textPrimary }}>
                Content Type
              </h3>
              <div className="flex gap-4">
                <div
                  onClick={() => setContentType('text')}
                  className="flex-1 p-4 rounded-lg cursor-pointer text-center"
                  style={{
                    border: contentType === 'text' ? `2px solid ${theme.buttonPrimary}` : `1px solid ${theme.borderColor}`,
                    background: contentType === 'text' ? theme.bgHover : 'transparent'
                  }}
                >
                  <Icon icon="mdi:text" className="text-2xl" style={{ color: theme.textPrimary }} />
                  <div className="text-[0.85rem] font-medium mt-2" style={{ color: theme.textPrimary }}>Text</div>
                </div>
                <div
                  onClick={() => setContentType('carousel')}
                  className="flex-1 p-4 rounded-lg cursor-pointer text-center"
                  style={{
                    border: contentType === 'carousel' ? `2px solid ${theme.buttonPrimary}` : `1px solid ${theme.borderColor}`,
                    background: contentType === 'carousel' ? theme.bgHover : 'transparent'
                  }}
                >
                  <Icon icon="mdi:image-multiple" className="text-2xl" style={{ color: theme.textPrimary }} />
                  <div className="text-[0.85rem] font-medium mt-2" style={{ color: theme.textPrimary }}>Carousel</div>
                </div>
              </div>
            </div>

            {/* Posting Times */}
            <div
              className="rounded-xl p-6"
              style={{ background: theme.bgSecondary, border: `1px solid ${theme.borderColor}` }}
            >
              <h3 className="m-0 mb-4 text-base font-semibold" style={{ color: theme.textPrimary }}>
                Posting Times
              </h3>
              <div className="grid grid-cols-5 gap-2">
                {timeSlots.map(time => (
                  <div
                    key={time}
                    onClick={() => handleTimeToggle(time)}
                    className="py-2.5 px-1 rounded-md cursor-pointer text-center text-[0.8rem] font-medium"
                    style={{
                      border: selectedTimes.includes(time) ? `2px solid ${theme.buttonPrimary}` : `1px solid ${theme.borderColor}`,
                      background: selectedTimes.includes(time) ? theme.bgHover : 'transparent',
                      color: selectedTimes.includes(time) ? theme.buttonPrimary : theme.textSecondary
                    }}
                  >
                    {time}
                  </div>
                ))}
              </div>
              {selectedTimes.length > 0 && (
                <div className="mt-4 p-3 bg-emerald-500/10 rounded-md text-[0.8rem] text-emerald-500">
                  {selectedTimes.length} times selected: {selectedTimes.sort().join(', ')}
                </div>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div>
            {/* Keywords */}
            <div
              className="rounded-xl p-6 mb-6"
              style={{ background: theme.bgSecondary, border: `1px solid ${theme.borderColor}` }}
            >
              <h3 className="m-0 mb-4 text-base font-semibold" style={{ color: theme.textPrimary }}>
                Content Keywords
              </h3>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newKeyword.trim()) {
                      setKeywords([...keywords, newKeyword.trim()]);
                      setNewKeyword('');
                    }
                  }}
                  placeholder="Add keyword..."
                  className="flex-1 py-3 px-4 rounded-lg text-[0.9rem] box-border"
                  style={{
                    border: `1px solid ${theme.borderColor}`,
                    background: theme.bgPrimary,
                    color: theme.textPrimary
                  }}
                />
                <Button
                  onClick={() => {
                    if (newKeyword.trim()) {
                      setKeywords([...keywords, newKeyword.trim()]);
                      setNewKeyword('');
                    }
                  }}
                  variant="primary"
                  icon="mdi:plus"
                  className="!py-3 !px-4"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {keywords.map((keyword, idx) => (
                  <span
                    key={idx}
                    className="py-2 px-4 bg-blue-500/10 rounded-full text-[0.85rem] text-blue-500 flex items-center gap-2"
                  >
                    {keyword}
                    <Icon
                      icon="mdi:close"
                      className="cursor-pointer opacity-60"
                      onClick={() => setKeywords(keywords.filter((_, i) => i !== idx))}
                    />
                  </span>
                ))}
                {keywords.length === 0 && (
                  <span className="text-[0.85rem]" style={{ color: theme.textTertiary }}>No keywords added yet</span>
                )}
              </div>
            </div>

            {/* Persona Settings */}
            <div
              className="rounded-xl p-6 mb-6"
              style={{ background: theme.bgSecondary, border: `1px solid ${theme.borderColor}` }}
            >
              <h3 className="m-0 mb-4 text-base font-semibold" style={{ color: theme.textPrimary }}>
                Persona Settings
              </h3>

              <div className="mb-4">
                <label className="text-[0.8rem] mb-2 block" style={{ color: theme.textSecondary }}>
                  Persona Type
                </label>
                <select
                  value={persona}
                  onChange={(e) => setPersona(e.target.value)}
                  className="w-full py-3 px-4 rounded-lg text-[0.9rem] cursor-pointer"
                  style={{
                    border: `1px solid ${theme.borderColor}`,
                    background: theme.bgPrimary,
                    color: persona ? theme.textPrimary : theme.textTertiary
                  }}
                >
                  <option value="">Select...</option>
                  <option value="professional">Professional</option>
                  <option value="creator">Content Creator</option>
                  <option value="entrepreneur">Entrepreneur</option>
                  <option value="educator">Educator</option>
                  <option value="consultant">Consultant</option>
                  <option value="influencer">Influencer</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="text-[0.8rem] mb-2 block" style={{ color: theme.textSecondary }}>
                  Expertise Field
                </label>
                <input
                  type="text"
                  value={expertiseField}
                  onChange={(e) => setExpertiseField(e.target.value)}
                  placeholder="e.g., AI, Marketing..."
                  className="w-full py-3 px-4 rounded-lg text-[0.9rem] box-border"
                  style={{
                    border: `1px solid ${theme.borderColor}`,
                    background: theme.bgPrimary,
                    color: theme.textPrimary
                  }}
                />
              </div>

              <div>
                <label className="text-[0.8rem] mb-2 block" style={{ color: theme.textSecondary }}>
                  Experience Level
                </label>
                <select
                  value={experienceLevel}
                  onChange={(e) => setExperienceLevel(e.target.value)}
                  className="w-full py-3 px-4 rounded-lg text-[0.9rem] cursor-pointer"
                  style={{
                    border: `1px solid ${theme.borderColor}`,
                    background: theme.bgPrimary,
                    color: experienceLevel ? theme.textPrimary : theme.textTertiary
                  }}
                >
                  <option value="">Select...</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="expert">Expert</option>
                  <option value="thought_leader">Thought Leader</option>
                </select>
              </div>
            </div>

            {/* Workflow Config Info (Read-only) */}
            {settings?.workflowConfig && Object.keys(settings.workflowConfig).some(k => settings.workflowConfig[k]) && (
              <div className="bg-violet-500/10 border border-violet-500/30 rounded-xl p-6">
                <h3 className="m-0 mb-4 text-base font-semibold text-violet-500 flex items-center gap-2">
                  <Icon icon="mdi:cog" />
                  Workflow Settings
                </h3>
                <div className="text-[0.85rem] text-violet-600 leading-7">
                  {settings.workflowConfig.source && <div>Source: {settings.workflowConfig.source}</div>}
                  {settings.workflowConfig.library && <div>Library: {settings.workflowConfig.library}</div>}
                  {settings.workflowConfig.style && <div>Style: {settings.workflowConfig.style}</div>}
                  {settings.workflowConfig.length && <div>Length: {settings.workflowConfig.length}</div>}
                  {settings.workflowConfig.theme && <div>Theme: {settings.workflowConfig.theme}</div>}
                  {settings.workflowConfig.imageStyle && <div>Image Style: {settings.workflowConfig.imageStyle}</div>}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-8 flex justify-end gap-4">
          <Button
            onClick={() => navigate('/projects')}
            variant="secondary"
            size="lg"
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleSave}
            loading={saving}
            loadingText="Saving..."
            icon="mdi:content-save"
            variant="primary"
            size="lg"
          >
            Save Changes
          </LoadingButton>
        </div>
      </div>
    </MainLayout>
  );
}

export default AutoManagementProjectPage;
